import Model from "./Model.js";
import Timespan from "./period/Timespan.js";

/**
 * A budget item holds a budgeted amount for an account and optional dimension
 * at a fiscal year month.
 *
 * @param {number} spec.accountNumber - Number of the account to budget
 * @param {number} [spec.amount] - Amount to budget
 * @param {Month} spec.month - Month for which the budgeted amount is
 * @param {string} spec.dimensionId - Id of the dimension the receiver applied to
 */
export default class BudgetItem extends Model {
	constructor({ accountNumber, month, dimensionId = "", amount = 0 } = {}) {
		super(...arguments);
		this._accountNumber = accountNumber;
		this._amount = amount;
		this._month = month;
		this._dimensionId = dimensionId;
	}

	getAccountNumber() {
		return this._accountNumber;
	}

	setAccountNumber(number) {
		this._accountNumber = number;
	}

	getMonth() {
		return this._month;
	}

	getDimensionId() {
		return this._dimensionId;
	}

	getAmount() {
		return this._amount;
	}

	setAmount(amount) {
		this._amount = amount;
	}

	hasAmount() {
		return !!this._amount;
	}

	cloneForMonth(month) {
		return new this.constructor({
			accountNumber: this._accountNumber,
			amount: this._amount,
			dimensionId: this._dimensionId,
			month,
		});
	}

	matches({ period, accountNumber, dimensionId }) {
		if (accountNumber && this.getAccountNumber() !== accountNumber) {
			return false;
		}

		if (period && !period.includes(this.getMonth())) {
			return false;
		}

		return (
			(dimensionId !== "" && !dimensionId) ||
			dimensionId === this._dimensionId
		);
	}

	_serializedProperties() {
		const data = super._serializedProperties();

		data.account = this._accountNumber;
		data.amount = this._amount;
		data.period = this._month.asTimespan();
		data.dimensions = [{ id: this._dimensionId }];

		return data;
	}

	static transformMaterializationData(data, context) {
		data.accountNumber = data.account;
		data.month = this.lookupMonth(data.period, context.getFiscalYear());

		// Sometimes we might get decimals from the server, but we only support
		// integer values in budget items.
		//
		// Not having to deal with decimals makes budgeting simpler for the
		// user, and decimals do not matter when budgeting.
		data.amount = Math.round(data.amount);

		// Because of historical reasons, the server dimensionId is stored in an
		// array. There must always at most one dimensionId per item, otherwise
		// just ignore extra dimensionIds.
		if (data.dimensions.length > 0) {
			data.dimensionId = data.dimensions[0].id;
		}
	}

	static lookupMonth(periodData, fiscalYear) {
		const timespan = Timespan.materialize(periodData);
		return fiscalYear.asMonths().find((month) => timespan.equates(month));
	}
}
